"use client";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { patchNotesData } from "./patchNotesData";
import { useHomeLanguage } from "./HomeLanguageContext";

export default function PatchNotes() {
  const { lang } = useHomeLanguage();
  const ITEMS_PER_PAGE = 6;
  const sectionRef = useRef<HTMLDivElement>(null);
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("revealed");
        });
      },
      { threshold: 0.08 }
    );
    sectionRef.current?.querySelectorAll(".animate-on-scroll").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const normalizedData = useMemo(() => {
    const clean = (text: string) =>
      text
        .replace(/\s+:/g, ":")
        .replace(/\s+,/g, ",")
        .replace(/\s+\./g, ".")
        .replace(/\s+\)/g, ")")
        .replace(/\(\s+/g, "(")
        .replace(/\s{2,}/g, " ")
        .trim();

    return patchNotesData.map((entry) => ({
      ...entry,
      items: entry.items.map(clean),
    }));
  }, []);

  const formatPatchDate = (date: string) => {
    const monthMap: Record<string, string> = {
      Januari: "Jan",
      Februari: "Feb",
      Maret: "Mar",
      April: "Apr",
      Mei: "Mei",
      Juni: "Jun",
      Juli: "Jul",
      Agustus: "Agu",
      September: "Sep",
      Oktober: "Okt",
      November: "Nov",
      Desember: "Des",
    };

    const m = date.trim().match(/^(\d{1,2})\s+([A-Za-z]+)\s+(\d{4})$/);
    if (!m) return date;

    const day = String(parseInt(m[1], 10));
    const month = monthMap[m[2]] || m[2];
    const year = m[3];

    return `${day} ${month} ${year}`;
  };

  const latestDate = formatPatchDate(normalizedData[0]?.date || "-");
  const totalItems = normalizedData.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / ITEMS_PER_PAGE));
  const startIdx = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIdx = Math.min(startIdx + ITEMS_PER_PAGE, totalItems);
  const paginatedData = normalizedData.slice(startIdx, endIdx);
  const pageButtonsMobile = useMemo(() => {
    const max = 5;
    let start = Math.max(1, currentPage - Math.floor(max / 2));
    let end = start + max - 1;
    if (end > totalPages) {
      end = totalPages;
      start = Math.max(1, end - max + 1);
    }
    return Array.from({ length: Math.max(0, end - start + 1) }, (_, i) => start + i);
  }, [currentPage, totalPages]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  useEffect(() => {
    setExpandedIdx(null);
  }, [currentPage]);

  const getHeadline = (items: string[]) => {
    const first = items[0] || "Update terbaru tersedia.";
    return first.length > 140 ? `${first.slice(0, 137)}...` : first;
  };

  const isId = lang === "id";

  return (
    <section id="patch-notes" className="py-24 lg:py-28 bg-white relative overflow-hidden" ref={sectionRef}>
      <div className="absolute top-0 left-0 right-0 h-px section-divider" />
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 right-0 w-96 h-96 bg-primary/3 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-0 w-80 h-80 bg-secondary/3 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-4 animate-on-scroll min-w-0">
            <span className="section-label mb-6 inline-flex">
              <span className="w-1.5 h-1.5 rounded-full bg-cta pulse-dot inline-block" />
              {isId ? "Patch Notes Lengkap" : "Full Patch Notes"}
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-fg leading-[1.1] tracking-[-0.03em] mb-4 mt-5">
              {isId ? "Riwayat Update " : "Update History "}
              <span className="gradient-text">Kolabo</span>
            </h2>
            <p className="text-fg-muted leading-relaxed mb-7 text-[15px]">
              {isId
                ? "Seluruh update patch notes ditampilkan lengkap seperti di kolabo.id, dengan format tanggal update agar mudah di-scan."
                : "Full patch notes are displayed like on kolabo.id, with date formatting that is easy to scan."}
            </p>

            <div className="p-6 rounded-xl bg-gradient-to-br from-brand-50 to-teal-light border border-primary/10 spotlight-card bento-card relative overflow-hidden">
              <div className="absolute top-0 right-0 w-24 h-24 bg-primary/6 rounded-full blur-xl pointer-events-none" />
              <div className="relative z-10 flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-gradient-brand flex items-center justify-center text-white shrink-0">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
                  </svg>
                </div>
                <div>
                  <p className="font-heading font-bold text-fg mb-1 text-sm tracking-tight">{isId ? "Sumber Resmi" : "Official Source"}</p>
                  <p className="text-fg-muted text-sm leading-relaxed">
                    {isId ? "Data patch notes diambil dari daftar pembaruan resmi Kolabo." : "Patch note data is taken from Kolabo's official update list."}
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-5 grid grid-cols-2 gap-4 max-w-sm">
              <div className="text-center">
                <div className="font-heading text-3xl font-bold gradient-text tracking-tight">{normalizedData.length}</div>
                <div className="text-fg-muted text-xs font-bold uppercase tracking-[0.12em] mt-0.5">{isId ? "Total Update" : "Total Updates"}</div>
              </div>
              <div className="text-center">
                <div className="font-heading text-lg font-bold gradient-text tracking-tight">{latestDate}</div>
                <div className="text-fg-muted text-xs font-bold uppercase tracking-[0.12em] mt-0.5">{isId ? "Update Terbaru" : "Latest Update"}</div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-8 animate-on-scroll min-w-0">
            <div className="pr-0 md:pr-2 flex flex-col gap-3 stagger-children min-w-0">
              {paginatedData.map((update, i) => {
                const absoluteIdx = startIdx + i;
                const headline = getHeadline(update.items);
                const details = update.items.slice(1);
                const isOpen = expandedIdx === absoluteIdx;

                return (
                <div
                  key={`${update.date}-${absoluteIdx}`}
                  className="rounded-xl border border-stone-100 bg-white shadow-card spotlight-card bento-card relative overflow-hidden group"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/2 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-xl" />

                  <div className="relative z-10 p-4 sm:p-5">
                    <button
                      type="button"
                      onClick={() => setExpandedIdx(isOpen ? null : absoluteIdx)}
                      className="w-full text-left flex items-start gap-3 sm:gap-4 min-w-0"
                    >
                      <span className="inline-flex items-center px-3 py-1 rounded-md border border-primary/15 bg-primary/6 text-primary text-xs font-bold tracking-wide shrink-0">
                        {formatPatchDate(update.date)}
                      </span>

                      <div className="min-w-0 flex-1">
                        <p className="text-[13px] sm:text-sm font-semibold text-fg leading-relaxed tracking-tight break-words">{headline}</p>
                        <p className="text-xs text-fg-muted mt-1.5">
                          {update.items.length} {isId ? "poin update" : "update points"}
                        </p>
                      </div>

                      <span
                        className={`text-primary text-sm font-bold transition-transform duration-300 shrink-0 ${isOpen ? "rotate-180" : ""}`}
                        aria-hidden="true"
                      >
                        ▾
                      </span>
                    </button>

                    {isOpen && details.length > 0 ? (
                      <ul className="mt-4 pt-4 border-t border-stone-100 space-y-2.5">
                        {details.map((item, j) => (
                          <li key={j} className="flex items-start gap-2 text-sm text-fg-muted leading-relaxed">
                            <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 shrink-0" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                </div>
                );
              })}
            </div>

            <div className="relative z-20 mt-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 min-w-0">
              <p className="text-fg-muted text-sm">
                {isId ? "Menampilkan " : "Showing "}
                <span className="font-semibold text-fg">{totalItems === 0 ? 0 : startIdx + 1}</span>{" - "}
                <span className="font-semibold text-fg">{endIdx}</span>
                {isId ? " dari " : " of "}
                <span className="font-semibold text-fg">{totalItems}</span> update
              </p>

              <div className="w-full sm:w-auto">
                <div className="sm:hidden w-full overflow-x-auto pb-1 [-webkit-overflow-scrolling:touch]">
                  <div className="inline-flex items-center gap-1.5 min-w-max px-0.5">
                    <button
                      type="button"
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-1.5 rounded-md border border-stone-200 bg-white text-fg text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed touch-manipulation"
                    >
                      {isId ? "Sebelumnya" : "Previous"}
                    </button>

                    {pageButtonsMobile[0] > 1 && (
                      <>
                        <button
                          type="button"
                          onClick={() => setCurrentPage(1)}
                          className="px-3 py-1.5 rounded-md border border-stone-200 bg-white text-fg text-sm font-semibold touch-manipulation"
                        >
                          1
                        </button>
                        {pageButtonsMobile[0] > 2 && <span className="px-1 text-sm text-fg-muted">...</span>}
                      </>
                    )}

                    {pageButtonsMobile.map((page) => (
                      <button
                        key={page}
                        type="button"
                        onClick={() => setCurrentPage(page)}
                        className={`px-3 py-1.5 rounded-md border text-sm font-semibold touch-manipulation ${
                          currentPage === page
                            ? "border-primary bg-primary text-white"
                            : "border-stone-200 bg-white text-fg"
                        }`}
                      >
                        {page}
                      </button>
                    ))}

                    {totalPages > pageButtonsMobile[pageButtonsMobile.length - 1] && (
                      <>
                        {totalPages > pageButtonsMobile[pageButtonsMobile.length - 1] + 1 && (
                          <span className="px-1 text-sm text-fg-muted">...</span>
                        )}
                        <button
                          type="button"
                          onClick={() => setCurrentPage(totalPages)}
                          className="px-3 py-1.5 rounded-md border border-stone-200 bg-white text-fg text-sm font-semibold touch-manipulation"
                        >
                          {totalPages}
                        </button>
                      </>
                    )}

                    <button
                      type="button"
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1.5 rounded-md border border-stone-200 bg-white text-fg text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed touch-manipulation"
                    >
                      {isId ? "Berikutnya" : "Next"}
                    </button>
                  </div>
                </div>

                <div className="hidden sm:flex items-center gap-1.5 flex-wrap">
                  <button
                    type="button"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1.5 rounded-md border border-stone-200 bg-white text-fg text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {isId ? "Sebelumnya" : "Previous"}
                  </button>

                  {Array.from({ length: totalPages }, (_, idx) => idx + 1).map((page) => (
                    <button
                      key={page}
                      type="button"
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-1.5 rounded-md border text-sm font-semibold ${
                        currentPage === page
                          ? "border-primary bg-primary text-white"
                          : "border-stone-200 bg-white text-fg"
                      }`}
                    >
                      {page}
                    </button>
                  ))}

                  <button
                    type="button"
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1.5 rounded-md border border-stone-200 bg-white text-fg text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {isId ? "Berikutnya" : "Next"}
                  </button>
                </div>
              </div>
            </div>

            <div className="text-center pt-5">
              <span className="text-fg-muted text-sm">
                {isId ? "Riwayat update lengkap terus bertambah — " : "The complete update history keeps growing — "}
                <a href="#daftar" className="text-primary font-semibold hover:underline">
                  {isId ? "Coba sekarang" : "Try now"} →
                </a>
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
